import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding } from "@/hooks/use-branding";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Bot, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";

/**
 * /reset-password?email=&token= — completes a password reset. The email +
 * token come from the link emailed by /api/auth/forgot-password. Works for
 * every password-based user type.
 */
export default function ResetPasswordPage() {
  const branding = useBranding();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { email, token } = useMemo(() => {
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    return { email: params.get("email") || "", token: params.get("token") || "" };
  }, []);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const linkValid = !!email && !!token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) {
      toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" });
      return;
    }
    if (password !== confirm) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest("POST", "/api/auth/reset-password", { email, token, newPassword: password });
      setDone(true);
      toast({ title: "Password reset", description: "You can now sign in with your new password." });
    } catch (err) {
      // Classify the backend error so each failure gets the right message.
      // Anti-enumeration is preserved: "Invalid request" (unknown/inactive user)
      // and "Invalid token." (bad/expired token) both map to the same generic
      // "link expired" message — neither reveals account state.
      const msg = err instanceof Error ? err.message.trim() : "";
      const isTokenError =
        !msg ||
        /invalid token/i.test(msg) ||
        /invalid request/i.test(msg);

      if (isTokenError) {
        toast({
          title: "Reset failed",
          description:
            "Reset link has expired. Please request a new password reset link.",
          variant: "destructive",
        });
      } else {
        // Password-policy or other validation error from the server — surface
        // it directly so the user knows what to fix (e.g. complexity rules).
        toast({
          title: "Reset failed",
          description: msg,
          variant: "destructive",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white px-4 py-10">
      <div className="absolute inset-x-0 top-0 -z-10 h-[360px] bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.10),_transparent_60%)]" />
      <div className="w-full max-w-md">
        <Link href="/" className="flex items-center justify-center gap-2 mb-6" data-testid="link-home">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 grid place-items-center text-white shadow-sm">
            <Bot className="w-5 h-5" />
          </span>
          <span className="font-semibold tracking-tight text-lg text-slate-900">{branding.shortName}</span>
        </Link>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {done ? (
              <div className="space-y-4 text-center">
                <span className="mx-auto w-12 h-12 rounded-full bg-emerald-50 grid place-items-center text-emerald-600">
                  <CheckCircle2 className="w-6 h-6" />
                </span>
                <h2 className="text-xl font-semibold" data-testid="text-reset-done-title">Password updated</h2>
                <p className="text-sm text-slate-500">Your password has been changed successfully.</p>
                <Button
                  onClick={() => navigate("/login")}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="button-go-login"
                >
                  Go to sign in
                </Button>
              </div>
            ) : !linkValid ? (
              <div className="space-y-4 text-center">
                <h2 className="text-xl font-semibold" data-testid="text-reset-invalid-title">Invalid reset link</h2>
                <p className="text-sm text-slate-500">
                  This link is missing required information or has expired. Request a new one to continue.
                </p>
                <Link href="/forgot-password" className="inline-block text-sm font-medium text-indigo-600 hover:underline" data-testid="link-request-new">
                  Request a new reset link →
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center space-y-2 mb-5">
                  <h2 className="text-2xl font-semibold" data-testid="text-reset-title">Set a new password</h2>
                  <p className="text-sm text-slate-500">
                    Choose a new password for <span className="font-medium text-slate-700">{email}</span>.
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-password">New password</Label>
                    <div className="relative">
                      <Input
                        id="reset-password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        placeholder="At least 8 characters"
                        className="pr-10"
                        data-testid="input-reset-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-confirm">Confirm new password</Label>
                    <Input
                      id="reset-confirm"
                      type={showPassword ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      autoComplete="new-password"
                      placeholder="Re-enter password"
                      data-testid="input-reset-confirm"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="button-reset-submit"
                  >
                    {submitting ? "Updating…" : "Reset password"}
                  </Button>
                </form>
                <Link href="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700 pt-4" data-testid="link-back-login">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
